import { useState, useEffect, useRef } from 'react';
import { DataTable, DataTableFilterMeta, DataTableSelectionSingleChangeEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Panel } from 'primereact/panel';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import Braid from '../../models/Braid';
import { BraidService } from '../../service/BraidService';
import { Button } from 'primereact/button';
import { Controller, useForm } from 'react-hook-form';
import { classNames } from 'primereact/utils';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import Stripe from '../../models/Stripe';
import { Link } from 'react-router-dom';
import { FilterMatchMode } from 'primereact/api';
import { Card } from 'primereact/card';

const BraidsView = () => {
    let emptyBraid: Braid = {
        id: 0,
        stylename: '',
        width: 0,
        fabric_Code_Id: '',
        hasSequences: false
    };

    const [braids, setBraids] = useState<Braid[]>([]);
    const [stripes, setStripes] = useState<Stripe[]>([]);
    const [loadingBraids, setLoadingBraids] = useState(true);
    const [braid, setBraid] = useState<Braid>(emptyBraid);
    const [deleteBraidDialog, setDeleteBraidDialog] = useState(false);
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [filters, setFilters] = useState<DataTableFilterMeta>({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const [editingRows, setEditingRows] = useState({});
    const isNew = useRef(false);
    const toast = useRef<any>(null);
    const dtBraids = useRef<any>(null);
        
    useEffect(() => {
        BraidService.getBraids().then(data => {
            setBraids(data);
            setLoadingBraids(false);
        });

        BraidService.getStripeList().then(data => {
            setStripes(data);
            setLoadingBraids(false);
        });
    }, []);

    const {
        control: controlBraid,
        getValues: braidGetValues,
        clearErrors: braidClearErrors,
        setError: braidSetError,
        formState: { errors: errorsBraid }, 
        reset: resetBraid,
        handleSubmit: handleSubmitBraid } = useForm({ mode: "all" });

    const onSubmitBraid = (data: any) => {
        isNew.current = true;        
        let _braid = { ...braid };
        _braid.stylename = data.stylename;
        _braid.fabric_Code_Id = data.fabric_Code_Id;
        _braid.width = data.width;

        let checkDuplicate: boolean = false;

        braids.forEach(b => {
            if (b.stylename === _braid.stylename && b.fabric_Code_Id === _braid.fabric_Code_Id && b.width === _braid.width) {
                checkDuplicate = true;
                return;
            }
        })
        
        if(!checkDuplicate) {
            saveBraid(_braid);
        } else {
            validateBraid();
            toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Duplicate braid entry detected! Please use unique values for stylename, width, and fabric.', life: 3000 });
        }
    };

    const getBraidFormErrorMessage = (name: string) => {
        if (Object.keys(errorsBraid).length > 0) {
            return errorsBraid[name] ? <small className="p-error">{(errorsBraid[name] as any).message}</small> : <small className="p-error">&nbsp;</small>;
        }
    };

    const hideDeleteBraidDialog = () => {
        setDeleteBraidDialog(false);
    };

    const saveBraid = (userBraidData: Braid) => {
        let _braids = [...braids];

        BraidService.addBraid(userBraidData).then((data) => {
            if (data) {
                _braids.push(data);
                setBraids(_braids);
                toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Braid Created', life: 3000 });
                setBraid(emptyBraid);
                _braids.sort(function (a, b) { return (a.stylename < b.stylename) ? -1 : (a.stylename < b.stylename) ? 1 : 0; });
                dtBraids.current.props.value = _braids;
                resetBraid();
            } else {
                toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to Create Braid, Check that you have entered a valid Braid', life: 3000 });
            }
        });
    }

    const confirmDeleteBraid = (braid: Braid) => {
        setBraid(braid);
        setDeleteBraidDialog(true);
    }

    const deleteBraid = () => {
        BraidService.deleteBraid(braid.id).then((data) => {
            if (data) {
                let _braids = braids.filter(val => val.id !== braid.id);
                setBraids(_braids);
                setDeleteBraidDialog(false);
                setBraid(emptyBraid);
                toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Braid Deleted', life: 3000 });
            } else {
                toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to Delete Braid', life: 3000 });
            }
        });
    }

    const setBraidRowIndex = (rowData: DataTableSelectionSingleChangeEvent<Braid[]>) => {
        let selectedBraidIndex = braids.findIndex(v => v.id === (rowData.value as Braid).id);
        let _editingRows = { ...editingRows, ...{ [`${braids[selectedBraidIndex].id}`]: true } };
        
        if(Object.keys(_editingRows).length === 1) {
            setEditingRows(_editingRows);
        }
    }

    const braidExpanderTemplate = (rowData: Braid) => {
        return (
            <Link className="pi pi-angle-right" to={`/braids/${rowData.id}`}></Link>
        )
    }

    const styleBodyTemplate = (rowData: Braid) => {
        return rowData.stylename;
    }

    const fabric_Code_IdBodyTemplate = (rowData: Braid) => {
        return rowData.fabric_Code_Id;
    }

    const widthBodyTemplate = (rowData: Braid) => {
        return rowData.width;
    }

    const rowEditorBraidTemplate = (rowData: Braid, props: any) => {
        return (
            !rowData.hasSequences
            ?
                <>
                    <Link className="pi pi-fw pi-trash rowDelete" title="Delete" onClick={() => confirmDeleteBraid(rowData)} to={''}></Link>
                </>
            : <></>
        );
    }

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let _filters = { ...filters };

        // @ts-ignore
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilter(value);
    };

    const headerBraidTable = (
        <div className="flex flex-column md:flex-row md:justify-content-end md:align-items-center">
            <IconField iconPosition="left">
                <InputIcon className="pi pi-search" />
                <InputText className="p-inputtext-sm" type="search" autoComplete="off" value={globalFilter} onChange={onGlobalFilterChange} placeholder="Search..." />
            </IconField>
        </div>
    );

    const deleteBraidDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteBraidDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteBraid} />
        </>
    );

    const validateBraid = () => {
        let _stripeStyle = braidGetValues("stylename");
        let _width = braidGetValues("width");
        let _fabricCodeId = braidGetValues("fabric_Code_Id");
        let _braid = braids.find((b) => b.stylename === _stripeStyle && b.width === _width && b.fabric_Code_Id === _fabricCodeId);

        if (_braid !== undefined && _braid !== null) {
            braidSetError("stylename", { type: "error", message: "Braid already exists" });
            braidSetError("width", { type: "error", message: "Braid already exists" });
            braidSetError("fabric_Code_Id", { type: "error", message: "Braid already exists" });
        } else {
            braidClearErrors();
        }

        return _braid === undefined || _braid === null;
    };

    return (
        <div className="grid-nogutter">
            <div className="col-12">
                <Toast ref={toast} />

                <Card className="layout-main-card border-noround">
                    <Panel header="Add Braid" className="addPanel mb-2">
                        <form key={1} onSubmit={handleSubmitBraid(onSubmitBraid)} className="p-fluid" autoComplete="off">
                            <div className="formgrid grid">
                                <div className="field col-2">
                                    <Controller
                                        name="stylename"
                                        control={controlBraid}
                                        rules={{
                                            required: 'Stripe Style is required.'
                                        }}
                                        render={({ field, fieldState }) => (
                                            <>
                                                <label htmlFor={field.name} className="font-bold text-xs">Stripe Style</label>
                                                <Dropdown value={field.value} options={stripes} optionLabel="stripe_Style" optionValue="stripe_Style" showClear className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => { field.onChange(e.value); }} filter filterBy="stripe_Style" placeholder="Select Stripe Style"/>
                                                {(field.value !== null && field.value !== undefined && !fieldState.error) ? <small className="text-primary">{stripes.find(ce => ce.stripe_Style === field.value)?.stripe_Desc}</small> : <></>}
                                                {getBraidFormErrorMessage('stylename')}
                                            </>
                                        )}
                                    />
                                </div>
                                <div className="field col-2">
                                    <Controller 
                                        name="width" 
                                        control={controlBraid}
                                        defaultValue={0}
                                        render={({ field, fieldState }) => (
                                            <>
                                                <label htmlFor={field.name} className="font-bold text-xs">Width</label>
                                                <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={field.value} onBlur={field.onBlur} onValueChange={(e) => { field.onChange(e); }} inputClassName={classNames({ 'p-invalid border-left-10': fieldState.error })} mode="decimal" min={0} maxFractionDigits={4} placeholder="Width" />
                                                {getBraidFormErrorMessage('width')}
                                            </>
                                        )} 
                                    />                                    
                                </div>
                                <div className="field col-2">
                                    <Controller 
                                        name="fabric_Code_Id" 
                                        control={controlBraid} 
                                        defaultValue=""
                                        render={({ field, fieldState }) => (
                                            <>
                                                <label htmlFor={field.name} className="font-bold text-xs">Fabric</label>
                                                <InputText id={field.name} name={field.name} value={field.value} maxLength={3} className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => { field.onChange(e.target.value); }} placeholder="Fabric" />
                                                {getBraidFormErrorMessage('fabric_Code_Id')}
                                            </>
                                        )} 
                                    />                                    
                                </div>
                                <div className="field field-buttons col-2 pt-4">
                                    <Button icon="pi pi-save" title="Save" rounded text severity="info" type="submit" />
                                    <Button icon="pi pi-undo" title="Undo" rounded text severity="danger" type="button" onClick={resetBraid}/>
                                </div>
                            </div>
                        </form>
                    </Panel>
    
                    <DataTable ref={dtBraids} value={braids} selectionMode="single" onSelectionChange={setBraidRowIndex}
                        dataKey="id" size="small" rowHover={true} loading={loadingBraids}
                        scrollable scrollHeight="calc(100vh - 23.8rem)"
                        paginator alwaysShowPaginator={false} rows={25} rowsPerPageOptions={[5, 10, 25]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Braid styles"
                        filters={filters} emptyMessage="No braid styles found." header={headerBraidTable}>
                        <Column body={braidExpanderTemplate} style={{ width: '3em' }} />
                        <Column field="stylename" header="Stripe Style" sortable body={styleBodyTemplate}  align="center" alignHeader="center"></Column>
                        <Column field="width" header="Width" sortable body={widthBodyTemplate}  align="center" alignHeader="center"></Column>
                        <Column field="fabric_Code_Id" header="Fabric" sortable body={fabric_Code_IdBodyTemplate}  align="center" alignHeader="center"></Column>
                        <Column rowEditor body={rowEditorBraidTemplate} bodyStyle={{ textAlign: 'center' }}></Column>
                    </DataTable>
                </Card>

                <Dialog visible={deleteBraidDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteBraidDialogFooter} onHide={hideDeleteBraidDialog}>
                    <div className="flex align-items-center justify-content-center">
                        <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                        {braid && <span>Are you sure you want to delete <b>{braid.stylename} - {braid.width} - {braid.fabric_Code_Id}</b>?</span>}
                    </div>
                </Dialog>
            </div>
        </div>
    );
  }

export default BraidsView;